import {plural, t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useId, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {sizing} from "@khanacademy/wonder-blocks-tokens";

import {RequestedInputLabel} from "./requested-input-label.tsx";

import minusCircle from "@phosphor-icons/core/bold/minus-circle-bold.svg";
import plusCircle from "@phosphor-icons/core/bold/plus-circle-bold.svg";

interface Props {
    label: string;
    value: string;
    onChange: (newValue: string) => void;
    description?: string;
    disabled?: boolean;
    required?: boolean;
    maxCharacters?: number;
    maxFields?: number;
    onValidate?: (errorMessage?: string | null) => unknown;
    error?: string | null;
}

interface RequestedInputSingleFieldProps {
    index: number;
    value: string;
    disabled?: boolean;
    onChange: (index: number, newValue: string) => void;
    removeField: () => void;
}

export const RequestedInputDynamicTextFields = ({
    label,
    onChange,
    value,
    description,
    disabled,
    required,
    maxCharacters,
    maxFields,
    onValidate,
    error,
}: Props): React.ReactElement => {
    const id = useId();

    const [fields, setFields] = useState<string[]>(
        value ? JSON.parse(value) : [""],
    );

    const shouldHideAdd =
        maxFields &&
        (fields.length === maxFields || fields.length >= maxFields);

    const onRemoveField = (index: number) => {
        const copyFields = [...fields];
        copyFields.splice(index, 1);
        const newFields = copyFields.length < 1 ? [""] : copyFields;
        setFields(newFields);
        onChange(JSON.stringify(newFields));
    };

    const validateText: (value: string) => string | null | void = (value) => {
        if (!maxCharacters || value.length <= maxCharacters) {
            return null;
        }
        return t`Uh oh! Input text is ${plural(value.length - maxCharacters, {
            one: "# character",
            other: "# characters",
        })} too long.`;
    };

    return (
        <RequestedInputLabel
            id={id}
            label={label}
            description={description}
            required={required}
            error={error}
        >
            <View style={styles.container}>
                <View style={styles.fieldList}>
                    {fields.map((fieldValue, index) => {
                        return (
                            <RequestedInputSingleField
                                key={index}
                                index={index}
                                value={fieldValue}
                                onChange={(index, newValue) => {
                                    const error = validateText(newValue);
                                    if (error) {
                                        onValidate?.(error);
                                    }
                                    const copyFields = [...fields];
                                    copyFields[index] = newValue;
                                    setFields(copyFields);
                                    onChange(JSON.stringify(copyFields));
                                }}
                                removeField={() => {
                                    onRemoveField(index);
                                }}
                            />
                        );
                    })}
                </View>
                <View style={styles.addAnotherField}>
                    <Button
                        style={shouldHideAdd && styles.hidden}
                        onClick={() => {
                            setFields([...fields, ""]);
                        }}
                        kind="tertiary"
                        startIcon={plusCircle}
                        aria-label={t`Add another`}
                        disabled={disabled}
                    >
                        {t`Add another`}
                    </Button>
                </View>
            </View>
        </RequestedInputLabel>
    );
};

const RequestedInputSingleField = (props: RequestedInputSingleFieldProps) => {
    const {value, index, onChange, disabled, removeField} = props;

    // First field is always disabled, since it can't be removed
    const isDisabled = disabled || index === 0;

    return (
        <View style={styles.fieldContainer}>
            <TextField
                value={value}
                disabled={disabled}
                onChange={(newValue) => {
                    onChange(index, newValue);
                }}
            />
            <IconButton
                aria-label={t`Remove field`}
                icon={minusCircle}
                style={styles.deleteButton}
                onClick={removeField}
                disabled={isDisabled}
                kind="tertiary"
                actionType="destructive"
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        width: "100%",
        gap: sizing.size_080,
    },
    fieldContainer: {
        flex: "1",
        flexDirection: "row",
        gap: sizing.size_080,
        justifyContent: "space-between",
    },
    fieldList: {
        gap: sizing.size_160,
    },
    addAnotherField: {
        display: "flex",
        width: "100%",
        alignItems: "start",
    },
    hidden: {
        display: "none",
    },
    deleteButton: {
        margin: 0,
    },
});
